import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} Antal varor vid senaste cart:update (omgång 86). */
  #lastCount = (() => {
    const n = parseInt(document.querySelector('cart-icon .cart-bubble__text-count')?.textContent || '', 10);
    return Number.isNaN(n) ? 0 : n;
  })();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      /* Outdoors Nordic (omgång 89, Victor 2026-11-01): historiksteget lades till när lådan öppnades,
         då sidan var låst (body fixed, scrollY 0). history.back() lät webbläsaren återställa det läget,
         så sidan bakom gled till toppen efter stängning (mätt: 900 → 0 px på Android och iPhone).
         Läget som dialogen just återställt behålls. */
      const y = window.scrollY;
      const prev = history.scrollRestoration;
      try { history.scrollRestoration = 'manual'; } catch (e) {}
      const keep = () => window.scrollTo({ top: y, behavior: 'instant' });
      window.addEventListener('popstate', () => {
        keep();
        requestAnimationFrame(() => { keep(); try { history.scrollRestoration = prev; } catch (e) {} });
      }, { once: true });
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    /* Outdoors Nordic (omgång 86, Victor 2026-10-31): 'cart:update' skickas vid ALLA ändringar,
       även borttagning och lådans egna justeringar (fraktförsäkring, gåva, BOGO-spärr). Lådan
       öppnades då på nytt när kunden tömt korgen och stängt den. Öppna bara när varorna blev fler
       och ändringen inte kommer från lådan själv. */
    const data = event.detail?.data || {};
    /* Köpknappen (product-form) skickar antalet TILLAGDA varor i itemCount, inte korgens antal:
       ett köp är alltid tillväxt, och korgens antal tas från den hämtade korgen. */
    const prev = this.#lastCount;
    const fromForm = data.source === 'product-form-component';
    const resCount = event.detail?.resource?.item_count;
    const count = fromForm
      ? (typeof resCount === 'number' ? resCount : (prev ?? 0) + (Number(data.itemCount) || 0))
      : (data.itemCount ?? resCount);
    if (typeof count === 'number' && !data.didError) this.#lastCount = count;
    const inner = ['cart-items-component', 'on-cart-ins', 'on-cart-gift', 'on-bogo-guard', 'on-variant-change'];
    const grew = fromForm || typeof count !== 'number' || prev == null || count > prev;
    if (this.hasAttribute('auto-open') && !data.didError && grew && !inner.includes(data.source)) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
